import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SessionView from "./SessionView";

/**
 * SessionsManager — keeps all visited sessions mounted simultaneously.
 *
 * Instead of destroying SessionView on navigation (killing WebRTC, Gladia,
 * AudioContext), we render ALL active sessions and toggle visibility.
 * Only the focused session outputs audio to speakers; background sessions
 * keep their WebRTC + Gladia connections alive (transcription continues).
 */
export default function SessionsManager() {
  const { id: focusedId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Track all session IDs that have been visited (keep them mounted)
  const [activeSessions, setActiveSessions] = useState<string[]>([]);

  // Add the focused session to the active set if not already there
  useEffect(() => {
    if (focusedId) {
      setActiveSessions((prev) =>
        prev.includes(focusedId) ? prev : [...prev, focusedId],
      );
    }
  }, [focusedId]);

  // Remove a session from the active set (called on destroy)
  const handleRemoveSession = useCallback(
    (sessionId: string) => {
      setActiveSessions((prev) => prev.filter((id) => id !== sessionId));
      // If we just removed the focused session, go back to list
      if (sessionId === focusedId) {
        navigate("/sessions");
      }
    },
    [focusedId, navigate],
  );

  return (
    <div style={{ width: "100%", height: "100%" }}>
      {activeSessions.map((sessionId) => (
        <div
          key={sessionId}
          style={{
            width: "100%",
            height: "100%",
            display: sessionId === focusedId ? "block" : "none",
          }}
        >
          <SessionView
            sessionId={sessionId}
            isFocused={sessionId === focusedId}
            onRemove={() => handleRemoveSession(sessionId)}
          />
        </div>
      ))}
    </div>
  );
}
